'use client';

/**
 * Forgot Password Form Component
 * 
 * Core component for requesting a password reset.
 * Can be styled by themes using className props and CSS variables.
 */

import { useState, useCallback, useRef, type FormEvent } from 'react';
import Link from 'next/link';
import { useCustomerAuth } from './CustomerAuthProvider';
import { Loader2, AlertCircle, CheckCircle2, ArrowLeft } from 'lucide-react';
import { TurnstileWidget, type TurnstileRef } from '@/components/ui/TurnstileWidget';

// ============================================================================
// Types
// ============================================================================

export interface ForgotPasswordFormProps {
  /** Callback after successful reset request */
  onSuccess?: () => void;
  /** Show "Back to login" link */
  showLoginLink?: boolean;
  /** Login page URL */
  loginUrl?: string;
  /** Custom class name for styling */
  className?: string;
  /** Custom title */
  title?: string;
  /** Custom subtitle */
  subtitle?: string;
}

// ============================================================================
// Component
// ============================================================================

export function ForgotPasswordForm({
  onSuccess,
  showLoginLink = true,
  loginUrl = '/login',
  className = '',
  title = 'Reset Password',
  subtitle = "Enter your email address and we'll send you a link to reset your password.",
}: ForgotPasswordFormProps) {
  const { resetPassword, error, clearError } = useCustomerAuth();

  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileRef>(null);

  const handleTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null);
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Clear previous errors
    setFormError(null);
    clearError();

    // Validation
    if (!email.trim()) {
      setFormError('Email is required');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setFormError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      await resetPassword(email.trim(), turnstileToken || undefined);
      setIsEmailSent(true);
      onSuccess?.();
    } catch (err) {
      console.error('[ForgotPasswordForm] Reset request failed:', err);
      // Note: We still show success even if the email doesn't exist
      // This is a security best practice to prevent email enumeration
      setIsEmailSent(true);
    } finally {
      setIsSubmitting(false);
      // Reset Turnstile — tokens are single-use
      setTurnstileToken(null);
      turnstileRef.current?.reset();
    }
  };

  const displayError = formError || error?.message;
  // Only disable during active submit - not during initial auth check.
  const isDisabled = isSubmitting;

  // Show success message
  if (isEmailSent) {
    return (
      <div className={`forgot-password-form forgot-password-form--success ${className}`}>
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mb-6">
            <CheckCircle2 className="h-8 w-8 text-success" />
          </div>
          <h1 className="text-2xl font-semibold text-foreground mb-2">
            Check your email
          </h1>
          <p className="text-muted-foreground mb-6">
            If an account exists for <strong>{email}</strong>, you&apos;ll receive
            a password reset link shortly.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => {
                setIsEmailSent(false);
                setEmail('');
              }}
              className="w-full h-12 rounded-xl border border-border bg-background text-foreground font-medium hover:bg-muted transition-colors"
            >
              Try a different email
            </button>
            <Link
              href={loginUrl}
              className="block w-full h-12 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center justify-center hover:bg-primary/90 transition-colors"
            >
              Back to login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`forgot-password-form ${className}`}>
      {/* Back Link */}
      {showLoginLink && (
        <Link
          href={loginUrl}
          className="forgot-password-form__back inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to login
        </Link>
      )}

      {/* Header */}
      <div className="forgot-password-form__header mb-8">
        <h1 className="forgot-password-form__title text-3xl font-semibold text-foreground">
          {title}
        </h1>
        <p className="forgot-password-form__subtitle text-base text-muted-foreground mt-2">
          {subtitle}
        </p>
      </div>

      {/* Error Alert */}
      {displayError && (
        <div className="forgot-password-form__error mb-6 flex items-start gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20">
          <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
          <p className="text-sm text-destructive">{displayError}</p>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="forgot-password-form__form space-y-5">
        {/* Email */}
        <div className="forgot-password-form__field">
          <label
            htmlFor="forgot-email"
            className="forgot-password-form__label block text-sm font-medium text-foreground mb-2"
          >
            Email address
          </label>
          <input
            id="forgot-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@example.com"
            disabled={isDisabled}
            className="forgot-password-form__input w-full h-12 px-4 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>

        {/* Turnstile Bot Protection */}
        <TurnstileWidget
          ref={turnstileRef}
          onVerify={handleTurnstileVerify}
          onExpire={handleTurnstileExpire}
          className="flex justify-center"
        />

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isDisabled}
          className="forgot-password-form__submit w-full h-12 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Sending...
            </span>
          ) : (
            'Send reset link'
          )}
        </button>
      </form>
    </div>
  );
}

export default ForgotPasswordForm;
